import { usePortalGuard } from '@glplus/glpweb-shared-libs';
import { Routes, Route } from 'react-router';
import { AppShell } from './components/AppShell';
import HomePage from './routes/HomePage';
import VendorApplications from './routes/VendorApplications';
import VendorApplicationsBoard from './routes/VendorApplicationsBoard';
import VendorApplicationReview from './routes/VendorApplicationReview';
import ManageVendors from './routes/ManageVendors';
import VendorReview from './routes/VendorReview';
import VendorProducts from './routes/VendorProducts';
import ManageVendorDetail from './routes/ManageVendorDetail';
import Login from './routes/Login';
import VendorSettlements from './routes/VendorSettlements';
import VendorDiscountReportReview from './routes/VendorDiscountReportReview';
import VendorAdjustmentReview from './routes/VendorAdjustmentReview';

const PORTAL_URLS = {
  consumer: import.meta.env.VITE_PORTAL_CONSUMER_URL as string,
  driver: import.meta.env.VITE_PORTAL_DRIVER_URL as string,
  vendor: import.meta.env.VITE_PORTAL_VENDOR_URL as string,
  admin: import.meta.env.VITE_PORTAL_ADMIN_URL as string,
};

const LOGIN_URL = import.meta.env.VITE_LOGIN_URL as string;

export default function App() {
  return (
    <Routes>
      {/* /login renders OUTSIDE the guard — the guard redirects here, so
          guarding it would loop. */}
      <Route path="/login" element={<Login />} />
      <Route path="/*" element={<GuardedApp />} />
    </Routes>
  );
}

function GuardedApp() {
  const guard = usePortalGuard({
    requiredPortal: 'admin',
    portalUrls: PORTAL_URLS,
    loginUrl: LOGIN_URL,
    // Pool-per-actor (2026-07-04): the portal authenticates against the
    // EMPLOYEE pool, which has NO groups — pool membership (admin-create-only)
    // is the perimeter, so any valid session is admitted. Fine-grained access
    // is enforced server-side by admin-api (GLPE row → capabilities), and the
    // nav renders from /admin/me/capabilities.
    admitAnyAuthenticated: true,
    // Local-iteration escape hatch (Runbook AM). Set VITE_DISABLE_PORTAL_GUARD=true
    // in .env.local to render the portal without auth. NEVER set in a deployed build.
    disabled: import.meta.env.VITE_DISABLE_PORTAL_GUARD === 'true',
  });

  if (guard.isLoading) {
    return <Loading message="Checking access…" />;
  }
  if (guard.status !== 'authorized') {
    // Hook is redirecting via window.location.assign. Render a brief
    // placeholder while the navigation commits.
    return <Loading message="Redirecting…" />;
  }

  return (
    <AppShell>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/vendor-products" element={<VendorProducts />} />
        <Route path="/vendor-applications" element={<VendorApplications />} />
        <Route path="/vendor-applications/board" element={<VendorApplicationsBoard />} />
        <Route path="/vendor-applications/:id" element={<VendorApplicationReview />} />
        <Route path="/vendor-review" element={<VendorReview />} />
        <Route path="/manage-vendors" element={<ManageVendors />} />
        <Route path="/manage-vendors/:id" element={<ManageVendorDetail />} />
        <Route path="/vendor-settlements" element={<VendorSettlements />} />
        <Route path="/vendor-settlements/discounts/:reportId" element={<VendorDiscountReportReview />} />
        <Route path="/vendor-settlements/adjustments/:adjustmentId" element={<VendorAdjustmentReview />} />
        <Route path="*" element={<HomePage />} />
      </Routes>
    </AppShell>
  );
}

function Loading({ message }: { message: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        fontFamily: 'system-ui, sans-serif',
        color: '#555',
      }}
    >
      {message}
    </div>
  );
}
